/* eslint-disable @typescript-eslint/no-this-alias */
import { getUrlTextsPackage as getUrlTextsPackages } from '../spdConfig';

import * as Configuration from './testConfiguration';
import { Loader } from './loader';

declare let testConfiguration: Configuration.TestConfiguration;

export type TextId = Text | string;

export type Text = {
  id?: string;
  text_presentation: string;
  text_read: string;
  sound?: string;
};

type TextPackage = {
  lange: Configuration.Lange;
  gender: Configuration.Gender;
  texts: Text[];
};

export type DbTexts = string[] | TextPackage[];

export class TextSelector {
  protected dbTexts: TextPackage[] = [];

  protected async getTextPackageFromCSV(url: string): Promise<DbTexts> {
    const csv = await Loader.getWithVersion(url);
    const rows = csv.split('\n');
    const header = rows[0].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
    const txtPackage: TextPackage = {
      lange: header[0].slice(1, -1),
      gender: header[1].slice(1, -1),
      texts: [],
    };

    for (const row of rows.slice(1)) {
      const attr = row.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g); ///wyodrębnienie poszczególnych członów wiersza
      if (attr && attr.length > 1) {
        const presentation = attr[1].slice(1, -1);
        const read =
          (attr[2] && attr[2].slice(1, -1)) ||
          presentation.replace(/<\/?((?!textid)[^>]+)(>|$)/g, ''); //usuwamy wszystkie tagi poza textid
        const sound = attr[3] && attr[3].slice(1, -1);
        txtPackage.texts.push({
          id: attr[0].slice(1, -1),
          text_presentation: presentation,
          text_read: read,
          sound: sound,
        });
      }
    }
    return [txtPackage];
  }

  protected async getTextPackage(url: string): Promise<DbTexts> {
    const ext = url.split('.')[1];
    if (ext === 'csv') {
      return await this.getTextPackageFromCSV(getUrlTextsPackages() + url);
    } else {
      return await Loader.getSyncJSONWithVersion(getUrlTextsPackages() + url);
    }
  }

  protected async getDbTexts(dbTexts: DbTexts): Promise<TextPackage[]> {
    let retDbTexts: TextPackage[] = [];
    for (const pack of dbTexts) {
      if (typeof pack === 'string') {
        const texts = await this.getTextPackage(pack);
        const packages = await this.getDbTexts(texts);
        retDbTexts = [...retDbTexts, ...packages];
        //DEBUG_MODE && console.log('getDbTexts', retDbTexts);
      } else {
        retDbTexts.push(pack);
      }
    }
    return retDbTexts;
  }

  public async loadDbTexts(url: string) {
    const texts = await this.getTextPackage(url);
    await this.setDbTexts(texts);
  }

  public async setDbTexts(dbTexts: DbTexts) {
    const that = this;
    that.dbTexts = await this.getDbTexts(dbTexts);
  }

  public getText(
    text: TextId,
    lange?: Configuration.Lange,
    gender?: Configuration.Gender,
  ): Text {
    return typeof text === 'string'
      ? this.getTextById(text, lange, gender)
      : text.id
      ? this.getTextById(text.id, lange, gender)
      : text;
  }

  protected replaceTagTextIds(
    text: Text,
    dbTexts: TextPackage[],
    lange: Configuration.Lange = Configuration.Lange.PL,
    gender: Configuration.Gender = Configuration.Gender.Male,
  ): Text {
    text.text_presentation = text?.text_presentation || text?.text_read;
    text.text_read = text?.text_read || text.text_presentation; // zabezpieczenie na przypadek, gdy teksty są json i nie podano text_read

    if (text.text_presentation && text.text_read) {
      const regex = /<textid=([\w\d_]+)\s*\/?>/g; //podmieniam teg textid=id na odpowiedni tekst
      text.text_presentation = text.text_presentation.replace(
        regex,
        (match, id) => {
          // Zwracamy tekst, który odpowiada wartości 'id'
          return this.getTextWithParam(id, dbTexts, lange, gender)
            .text_presentation;
        },
      );
      text.text_read = text.text_read.replace(regex, (match, id) => {
        // Zwracamy tekst, który odpowiada wartości 'id'
        return this.getTextWithParam(id, dbTexts, lange, gender).text_read;
      });
    }
    return text;
  }

  // eslint-disable-next-line complexity
  public getTextWithParam(
    id: string,
    dbTexts: TextPackage[],
    lange: Configuration.Lange = Configuration.Lange.PL,
    gender: Configuration.Gender = Configuration.Gender.Male,
  ) {
    for (const db of dbTexts) {
      if (db.gender === gender && db.lange === lange) {
        for (const text of db.texts) {
          if (text.id === id) {
            return this.replaceTagTextIds(text, dbTexts, lange, gender);
          }
        }
      }
    }
    for (const db of dbTexts) {
      // nie było w tym języku tej płci
      if (db.lange === lange) {
        for (const text of db.texts) {
          if (text.id === id) {
            return this.replaceTagTextIds(text, dbTexts, lange, gender);
          }
        }
      }
    }
    for (const db of dbTexts) {
      // nie ma tekstu dla tego języka
      for (const text of db.texts) {
        if (text.id === id) {
          return this.replaceTagTextIds(text, dbTexts, lange, gender);
        }
      }
    }
    return { text_presentation: id, text_read: id };
  }

  public getCurrentLange() {
    return testConfiguration.lange;
  }

  protected getTextById(
    id: string,
    lange?: Configuration.Lange,
    gender?: Configuration.Gender,
  ) {
    return this.getTextWithParam(
      id,
      this.dbTexts,
      lange || testConfiguration.lange,
      gender || testConfiguration.gender,
    );
  }
}
